/**
 * Subtipo de questão dissertativa — dimensão compartilhada entre a Biblioteca de
 * Questões (que cadastra) e a Correção de Provas (que corrige contra o que foi
 * cadastrado). Vive em `shared` porque as duas features dependem das mesmas
 * regras; duplicá-las deixaria o formulário e a correção divergirem.
 */

/**
 * ID do componente `MultilineText` (questão dissertativa) em
 * `GET /api/v1/exam/component-types`. Único tipo que aceita subtipo.
 */
export const MULTILINE_TEXT_COMPONENT_TYPE = 4;

/**
 * Subtipos de questão dissertativa. Espelha o enum `EExamItemSubtype` do
 * backend, documentado em `POST /api/v1/exam/question-library/ai-correction`.
 * As opções exibidas no formulário vêm de `GET /api/v1/exam/exam-item-sub-types`
 * — este enum existe apenas para as regras condicionais de campo.
 */
export enum EExamItemSubtype {
  /** Artigo de opinião — a IA avalia tese, argumentação e posicionamento. */
  OpinionArticle = 1,
  /** Dissertação — a IA avalia conteúdo, completude, coesão e gramática. */
  Essay = 2,
  /** Dissertação com resposta-modelo — exige `modelAnswer`; aceita `keyTerms`. */
  ModelAnswerEssay = 3,
  /** Caso MBA — exige `modelAnswer`, que carrega as Notas de Ensino. */
  MbaCase = 4,
}

/** Limite de caracteres de `modelAnswer` e `keyTerms` no contrato do backend. */
export const SUBTYPE_TEXT_MAX_LENGTH = 1000;

/**
 * Indica se o tipo de componente aceita subtipo (apenas `MultilineText`).
 *
 * @param componentType - ID do tipo de componente da questão
 * @returns `true` quando o campo de subtipo deve ser exibido e enviado
 */
export function supportsSubtype(componentType: number | null | undefined): boolean {
  return componentType === MULTILINE_TEXT_COMPONENT_TYPE;
}

/**
 * Indica se o subtipo exige resposta-modelo (`modelAnswer`) preenchida.
 * `ModelAnswerEssay` usa a resposta-modelo como referência de correção;
 * `MbaCase` usa o mesmo campo para as Notas de Ensino.
 *
 * @param subtype - ID do subtipo selecionado
 * @returns `true` quando `modelAnswer` é obrigatório
 */
export function requiresModelAnswer(subtype: number | null | undefined): boolean {
  return subtype === EExamItemSubtype.ModelAnswerEssay || subtype === EExamItemSubtype.MbaCase;
}

/**
 * Indica se o subtipo aceita termos-chave (`keyTerms`) — apenas `ModelAnswerEssay`.
 *
 * @param subtype - ID do subtipo selecionado
 * @returns `true` quando o campo de termos-chave deve ser exibido e enviado
 */
export function supportsKeyTerms(subtype: number | null | undefined): boolean {
  return subtype === EExamItemSubtype.ModelAnswerEssay;
}

/**
 * Indica se `modelAnswer` carrega Notas de Ensino em vez de uma resposta-modelo.
 * O campo é o mesmo no contrato; só o significado (e o rótulo na tela) muda.
 *
 * @param subtype - ID do subtipo da questão
 * @returns `true` no subtipo `MbaCase`
 */
export function isTeachingNotesSubtype(subtype: number | null | undefined): boolean {
  return subtype === EExamItemSubtype.MbaCase;
}

// ─── Termos-chave ─────────────────────────────────────────────────────────────
// O backend guarda `KeyTerms` como uma única string separada por vírgulas.

/** Separador usado na serialização enviada ao backend. */
const KEY_TERMS_SEPARATOR = ', ';

/**
 * Converte a string de termos-chave do backend em lista de termos, descartando
 * entradas vazias e espaços nas bordas.
 *
 * @param value - Termos separados por vírgula (ex.: `'liquidez, fluxo de caixa'`)
 * @returns Lista de termos normalizados
 *
 * @example
 * ```ts
 * parseKeyTerms(' liquidez ,, fluxo de caixa '); // ['liquidez', 'fluxo de caixa']
 * ```
 */
export function parseKeyTerms(value: string | null | undefined): string[] {
  return (value ?? '')
    .split(',')
    .map(term => term.trim())
    .filter(term => term.length > 0);
}

/**
 * Serializa a lista de termos no formato esperado pelo backend.
 *
 * @param terms - Lista de termos
 * @returns Termos unidos por vírgula, ou string vazia quando não há termos
 */
export function serializeKeyTerms(terms: readonly string[]): string {
  return terms.join(KEY_TERMS_SEPARATOR);
}

/**
 * Indica se o termo já existe na lista, ignorando caixa e espaços — evita chips
 * duplicados como `Liquidez` e `liquidez`.
 *
 * @param terms - Termos já adicionados
 * @param candidate - Termo que o usuário está adicionando
 * @returns `true` quando o termo já está na lista
 */
export function hasKeyTerm(terms: readonly string[], candidate: string): boolean {
  const normalized = candidate.trim().toLocaleLowerCase();
  return terms.some(term => term.trim().toLocaleLowerCase() === normalized);
}

/**
 * Acrescenta um termo à lista, ignorando termos vazios, duplicados e os que
 * estourariam o limite de caracteres da string serializada.
 *
 * @param terms - Termos já adicionados
 * @param candidate - Termo que o usuário está adicionando
 * @param maxLength - Limite de caracteres da string serializada
 * @returns Nova lista, ou a original quando o termo é recusado
 *
 * @example
 * ```ts
 * addKeyTerm(['liquidez'], 'ebitda', 1000); // ['liquidez', 'ebitda']
 * ```
 */
export function addKeyTerm(
  terms: readonly string[],
  candidate: string,
  maxLength: number,
): string[] {
  const trimmed = candidate.trim();
  if (trimmed.length === 0 || hasKeyTerm(terms, trimmed)) return [...terms];

  const next = [...terms, trimmed];
  if (serializeKeyTerms(next).length > maxLength) return [...terms];

  return next;
}
